/**
 * Which theme applies, and how to answer that before React mounts on web.
 *
 * `resolveThemeName` is the single rule ThemeContext and the boot paths share:
 * a theme the user picked wins, 'auto' follows the OS, and otherwise the
 * default depends on whether this device has a signed-in session — dark for the
 * app, light for the logged-out marketing pages.
 *
 * Two places have to answer before hydration or the page visibly repaints: the
 * inline script `+html.jsx` puts in <head> (sets the `theme` attribute the CSS
 * selectors key off) and `app/styles/index.web.js` (paints html/body).
 */
import { Themes } from '@/constants/Colors';
import { DEFAULT_APP_THEME, DEFAULT_PUBLIC_THEME } from '@/constants/config';

/**
 * The active theme name.
 *
 * `storedTheme` is the value the user explicitly chose ('auto' included), or
 * null/undefined when they never chose one.
 */
export function resolveThemeName({ storedTheme, isSignedIn, prefersDark }) {
  if (storedTheme === 'auto') return prefersDark ? 'black' : 'white';
  if (storedTheme && Themes[storedTheme]) return storedTheme;
  return isSignedIn ? DEFAULT_APP_THEME : DEFAULT_PUBLIC_THEME;
}

/** Background of each theme's primary surface, for the pre-hydration paint. */
export const THEME_BACKGROUNDS = {
  white: '#ffffff',
  ocean: '#F0F8FF',
  pastel: '#FFF5F9',
  mint: '#F2FAF6',
  paper: '#FAF7F0',
  muted: '#242526',
  sepia: '#2C261D',
  night: '#1A1D2E',
  smoke: '#13171B',
  black: '#000000',
};

export const THEME_STORAGE_KEY = 'theme';
export const ACCENT_BG_STORAGE_KEY = 'accentBackgroundColor';
export const ACCENT_TEXT_STORAGE_KEY = 'accentTextColor';

/** Zustand persist key for the auth store; `state.token` is set while signed in. */
const AUTH_STORAGE_KEY = 'auth-storage';

function readLocalStorage(key) {
  try {
    return window.localStorage.getItem(key);
  } catch (_) {
    // Private browsing and blocked third-party storage both throw here.
    return null;
  }
}

/** True when a token is already persisted, i.e. this device is in the app. */
export function hasSignedInSession() {
  const raw = readLocalStorage(AUTH_STORAGE_KEY);
  if (!raw) return false;
  try {
    return !!JSON.parse(raw)?.state?.token;
  } catch (_) {
    return false;
  }
}

function prefersDarkOS() {
  try {
    return !!window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
  } catch (_) {
    return false;
  }
}

/** Stored theme choice, or null when the user has never picked one. */
export function readStoredThemePreference() {
  const stored = readLocalStorage(THEME_STORAGE_KEY);
  if (stored && (Themes[stored] || stored === 'auto')) return stored;
  return null;
}

export function readStoredAccentPreference(key) {
  return readLocalStorage(key);
}

/** The theme name to paint with before ThemeContext takes over. */
export function resolveBootTheme() {
  return resolveThemeName({
    storedTheme: readStoredThemePreference(),
    isSignedIn: hasSignedInSession(),
    prefersDark: prefersDarkOS(),
  });
}

/**
 * The same resolution as an inline <script> body. It has to be self-contained
 * source text because it runs before any bundle has loaded, so the constants
 * above are baked in rather than imported.
 */
export const BOOT_THEME_SCRIPT = `
(function() {
  function read(key) {
    try { return localStorage.getItem(key); } catch (e) { return null; }
  }
  var backgrounds = ${JSON.stringify(THEME_BACKGROUNDS)};
  var stored = read('${THEME_STORAGE_KEY}');
  var theme;
  if (stored === 'auto') {
    var prefersDark = false;
    try {
      prefersDark = !!window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    } catch (e) {}
    theme = prefersDark ? 'black' : 'white';
  } else if (stored && backgrounds[stored]) {
    theme = stored;
  } else {
    var signedIn = false;
    var raw = read('${AUTH_STORAGE_KEY}');
    if (raw) {
      try {
        var parsed = JSON.parse(raw);
        signedIn = !!(parsed && parsed.state && parsed.state.token);
      } catch (e) {}
    }
    theme = signedIn ? '${DEFAULT_APP_THEME}' : '${DEFAULT_PUBLIC_THEME}';
  }
  document.documentElement.setAttribute('theme', theme);
  var background = backgrounds[theme];
  if (background) {
    document.documentElement.style.backgroundColor = background;
  }
})();
`;
